/* eslint-disable consistent-return */
/* eslint-disable no-restricted-globals */
import React, { useCallback, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
} from '@mui/material';
import { HashRouter, useLocation } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import useMediaQuery from '@mui/material/useMediaQuery';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import TopBar from './features/navigation/components/TopBar';
import Viewer from './features/navigation/components/Viewer';
import { useGetConfigQuery } from './features/purchase-page/services/purchase.endpoints';
import { useAppDispatch, useAppSelector } from './shared/state/hooks';
import {
  selectUser,
  selectIsLoggedIn,
} from './features/auth/state/userSelectors';
import apiSlice, { tagTypes } from './shared/state/apiSlice';
import Footer from './features/navigation/components/Footer';
import ScrollToTop from './features/navigation/components/ScrollToTop';
import AdminNavBar from './features/navigation/components/AdminNavBar';

const theme = createTheme({
  palette: {
    primary: { main: '#37474F' },
    // CTA blue — used for purchase actions (Buy Now, checkout).
    secondary: { main: '#1976D2' },
  },
  // typography: {
  //   button: {
  //     textTransform: 'none',
  //     fontWeight: 600,
  //   },
  // },
  // components: {
  //   MuiButton: {
  //     defaultProps: {
  //       disableElevation: true,
  //     },
  //   },
  // },
});

function MainContainer({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const isFullWidthPage =
    pathname.startsWith('/transactions') ||
    pathname.startsWith('/users') ||
    pathname.startsWith('/dashboard');

  return (
    <Container maxWidth={isFullWidthPage ? false : 'lg'} sx={{ p: 4 }}>
      {children}
    </Container>
  );
}

export default function App() {
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const { data: config } = useGetConfigQuery();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const user = useAppSelector(selectUser);
  const isAdmin = user?.role === 'admin';

  const showActivateCallback = useCallback((data: unknown) => {
    console.log('activate:show', data);
  }, []);

  // --- your GA param stripper, React style ---
  useEffect(() => {
    const GA_PARAMS = ['_gl', '_ga', '_ga_S7SRH6J0P7', '_gcl_au'];

    function stripParamsOnce() {
      try {
        const url = new URL(window.location.href);
        let changed = false;
        GA_PARAMS.forEach((p) => {
          if (url.searchParams.has(p)) {
            url.searchParams.delete(p);
            changed = true;
          }
        });
        if (changed) {
          history.replaceState(
            {},
            '',
            url.pathname +
              (url.searchParams.toString()
                ? `?${url.searchParams.toString()}`
                : '') +
              window.location.hash,
          );
        }
      } catch (e) {
        console.error('Failed to strip GA params:', e);
      }
    }

    const onLoad = () => setTimeout(stripParamsOnce, 250);
    window.addEventListener('load', onLoad);
    window.addEventListener('hashchange', stripParamsOnce);

    return () => {
      window.removeEventListener('load', onLoad);
      window.removeEventListener('hashchange', stripParamsOnce);
    };
  }, []);
  // -------------------------------------------

  useEffect(() => {
    if (window.electron) {
      return window.electron.ipcRenderer.on(
        'activation:show',
        showActivateCallback,
      );
    }
  }, [showActivateCallback]);

  useEffect(() => {
    if (!isLoggedIn) {
      dispatch(apiSlice.util.invalidateTags(tagTypes));
    }
  }, [dispatch, isLoggedIn]);

  if (!config) return null;

  const { stripeClientToken } = config;
  const stripePromise = loadStripe(stripeClientToken);

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <HashRouter>
          <ScrollToTop />
          <Elements stripe={stripePromise}>
            <CssBaseline />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  transition: 'margin-left 0.3s ease',
                }}
              >
                <TopBar />
                {isAdmin && isMdUp && <AdminNavBar />}
                <MainContainer>
                  <Viewer />
                </MainContainer>
              </Box>
              <Footer />
            </Box>
          </Elements>
        </HashRouter>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
